import {
	Shield,
	Lock,
	Info,
	Globe,
	Mail,
	Loader2,
	Sparkles,
	FileText,
	X,
	Phone,
	User2,
	Building2,
	Facebook,
	Linkedin,
	Twitter,
	Instagram,
	Youtube,
	ExternalLink,
} from "lucide-react";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
	addDoc,
	collection,
	doc,
	serverTimestamp,
	setDoc,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { appId, db, storage } from "@/lib/firebase";
import { BrandLogo } from "./BrandLogo";
import type { NavGroup, NavItem, FooterConfig, FooterSocialLink } from "@/lib/cms";
import { NavIconRenderer } from "@/lib/nav-icons";
import { replaceCmsPlaceholders, type CMSPlaceholderReplacements } from "@/lib/cms-placeholders";
import { NewsletterSubscribeForm } from "./NewsletterSubscribeForm";

/** Contact info from app settings (optional). */
interface ContactInfo {
	contactPhone?: string;
	contactEmail?: string;
	companyAddress?: string;
}

interface FooterProps {
	brandName?: string;
	brandTagline?: string;
	contact?: ContactInfo;
	/** When true, links and form are non-interactive (e.g. for CMS preview). */
	linksDisabled?: boolean;
	/** Dynamic footer config from CMS. */
	footerConfig?: FooterConfig | null;
	/** Dynamic nav groups from CMS (footer placement). */
	navGroups?: NavGroup[];
	/** Dynamic nav items from CMS (footer placement). */
	navItems?: NavItem[];
	/** If set, renders this raw HTML instead of the default React footer. */
	footerHtml?: string | null;
	/** For substituting {{phone-number}}, etc. in nav item HTML snippets. */
	placeholderReplacements?: CMSPlaceholderReplacements | null;
}

function phoneToTelHref(displayPhone: string): string {
	const digits = displayPhone.replace(/\D/g, '');
	return digits ? `tel:+${digits}` : 'tel:';
}

const SOCIAL_ICON: Record<FooterSocialLink['platform'], React.ReactNode> = {
	facebook: <Facebook size={20} />,
	linkedin: <Linkedin size={20} />,
	twitter: <Twitter size={20} />,
	instagram: <Instagram size={20} />,
	youtube: <Youtube size={20} />,
	tiktok: <TikTokIcon size={20} />,
};

const SOCIAL_LABEL: Record<FooterSocialLink['platform'], string> = {
	facebook: 'Facebook',
	linkedin: 'LinkedIn',
	twitter: 'Twitter / X',
	instagram: 'Instagram',
	youtube: 'YouTube',
	tiktok: 'TikTok',
};

/** Default social links when footer config has none. Order: TikTok, Facebook, LinkedIn, Instagram, YouTube. */
const DEFAULT_SOCIAL_LINKS: { url: string; platform: FooterSocialLink['platform'] }[] = [
	{ url: 'https://tiktok.com/@smbprime', platform: 'tiktok' },
	{ url: 'https://www.facebook.com/smbprime', platform: 'facebook' },
	{ url: 'https://www.linkedin.com/company/smbprime/', platform: 'linkedin' },
	{ url: 'https://www.instagram.com/smbprimeai', platform: 'instagram' },
	{ url: 'https://www.youtube.com/@smbprimeai', platform: 'youtube' },
];

/** TikTok icon (outline style) — Lucide does not provide a TikTok icon. */
function TikTokIcon({ size = 20 }: { size?: number }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<path d="M9 12a4 4 0 0 0 4 4h0a4 4 0 0 0 4-4V4" />
			<path d="M17 2h-2a3 3 0 0 0-3 3v2" />
		</svg>
	);
}

export const Footer = ({
	brandName,
	brandTagline,
	contact,
	linksDisabled = false,
	footerConfig = null,
	navGroups = [],
	navItems = [],
	footerHtml = null,
	placeholderReplacements = null,
}: FooterProps) => {
	// If a custom HTML override is set, render it directly instead of the React template.
	if (footerHtml && footerHtml.trim()) {
		return <div dangerouslySetInnerHTML={{ __html: footerHtml }} />;
	}
	const effectiveBrandName = brandName || "SMBPrime";
	const effectiveTagline = brandTagline || "Government contracting made simple";
	const displayPhone = contact?.contactPhone || "+1 832-542-6260";
	const telHref = phoneToTelHref(displayPhone);

	// Dynamic config values with fallbacks
	const showLeadCaptureBanner = footerConfig ? footerConfig.showLeadCaptureBanner : true;
	const showWebsiteCTABand = footerConfig ? footerConfig.showWebsiteCTABand : true;
	const showNewsletterSection = footerConfig?.showNewsletterSection === true;
	const newsletterHeading = footerConfig?.newsletterHeading || undefined;
	const newsletterDescription = footerConfig?.newsletterDescription || undefined;
	const copyrightText = footerConfig?.copyrightText ?? `© ${new Date().getFullYear()} SMBPrime.ai. All rights reserved.`;
	const brandDescription = footerConfig?.brandDescription ?? `${effectiveTagline}. SMBPrime helps small businesses navigate government contracting.`;
	const socialLinks = footerConfig?.socialLinks ?? null; // null = use hardcoded FB/LinkedIn
	const savedFooterNavGroupIds = footerConfig?.footerNavGroupIds ?? [];

	// Build ordered footer column IDs: use saved config order, then append any footer-placement
	// groups not yet in config (e.g. newly added "Support") so new columns appear without re-saving Footer Config.
	const footerPlacementGroups = navGroups.filter((g) => g.placement === "footer");
	const effectiveFooterNavGroupIds =
		savedFooterNavGroupIds.length > 0
			? [
					...savedFooterNavGroupIds.filter((id) => footerPlacementGroups.some((g) => g.id === id)),
					...footerPlacementGroups
						.filter((g) => !savedFooterNavGroupIds.includes(g.id))
						.sort((a, b) => a.order - b.order)
						.map((g) => g.id),
				]
			: footerPlacementGroups.sort((a, b) => a.order - b.order).map((g) => g.id);

	// Build ordered footer columns from CMS nav groups
	const footerColumns: { group: NavGroup; items: NavItem[] }[] = (() => {
		if (effectiveFooterNavGroupIds.length === 0) return [];
		return effectiveFooterNavGroupIds
			.map((id) => navGroups.find((g) => g.id === id))
			.filter((g): g is NavGroup => !!g && g.isVisible)
			.map((group) => ({
				group,
				items: navItems
					.filter((it) => it.groupId === group.id && !it.parentId && it.isVisible)
					.sort((a, b) => a.order - b.order),
			}));
	})();
	const displayEmail = contact?.contactEmail?.trim();
	const displayAddress = contact?.companyAddress?.trim();
	const [websiteUrl, setWebsiteUrl] = useState("");
	const [email, setEmail] = useState("");
	// Anti-spam: honeypot field value (should always stay empty for real users)
	const [leadHoneypot, setLeadHoneypot] = useState("");
	// Anti-spam: timestamp when the lead form first rendered (used for timing check)
	const leadFormMountTime = useRef(Date.now());
	// Anti-spam: localStorage key + cooldown window (2 min)
	const LEAD_COOLDOWN_KEY = "smbprime_scan_last_ts";
	const LEAD_COOLDOWN_MS = 120_000;
	const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isServiceSubmitting, setIsServiceSubmitting] = useState(false);
	const [submitMessage, setSubmitMessage] = useState<{
		type: "success" | "error";
		text: string;
	} | null>(null);
	const [serviceSubmitMessage, setServiceSubmitMessage] = useState<{
		type: "success" | "error";
		text: string;
	} | null>(null);
	const [capabilityStatementFile, setCapabilityStatementFile] = useState<File | null>(null);
	const [serviceForm, setServiceForm] = useState({
		companyName: "",
		contactName: "",
		contactEmail: "",
		contactPhone: "",
		websiteUrl: "",
		requestType: "new_website" as
			| "new_website"
			| "website_enhancement"
			| "redesign_and_enhancement",
		briefRequest: "",
	});

	const normalizeWebsiteUrl = (rawUrl: string): string => {
		const trimmed = rawUrl.trim();
		if (!trimmed) return "";
		if (/^https?:\/\//i.test(trimmed)) return trimmed;
		return `https://${trimmed}`;
	};

	const resetWebsiteServiceForm = () => {
		setCapabilityStatementFile(null);
		setServiceSubmitMessage(null);
		setServiceForm({
			companyName: "",
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			websiteUrl: "",
			requestType: "new_website",
			briefRequest: "",
		});
	};

	const closeWebsiteServiceModal = () => {
		setIsServiceModalOpen(false);
		if (!isServiceSubmitting) {
			resetWebsiteServiceForm();
		}
	};

	useEffect(() => {
		if (!isServiceModalOpen) return undefined;
		const originalOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isServiceSubmitting) {
				setIsServiceModalOpen(false);
				resetWebsiteServiceForm();
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => {
			document.body.style.overflow = originalOverflow;
			document.removeEventListener("keydown", handleEscape);
		};
	}, [isServiceModalOpen, isServiceSubmitting]);

	const handleLeadSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setSubmitMessage(null);

		// Layer 1 — Honeypot: if the hidden field has any value a bot filled it in.
		// Silently "succeed" without writing to Firestore.
		if (leadHoneypot.trim()) {
			setWebsiteUrl("");
			setEmail("");
			setSubmitMessage({
				type: "success",
				text: "Request received! We'll email your federal readiness ranking soon.",
			});
			return;
		}

		// Layer 2 — Timing check: legitimate users take at least 1.5s to fill the form.
		if (Date.now() - leadFormMountTime.current < 1500) {
			setSubmitMessage({
				type: "error",
				text: "Please review your inputs and try again.",
			});
			return;
		}

		// Layer 3 — localStorage rate limit: prevent rapid resubmission from the same browser.
		try {
			const last = localStorage.getItem(LEAD_COOLDOWN_KEY);
			if (last && Date.now() - Number(last) < LEAD_COOLDOWN_MS) {
				// Silently "succeed" — they already submitted recently.
				setWebsiteUrl("");
				setEmail("");
				setSubmitMessage({
					type: "success",
					text: "Request received! We'll email your federal readiness ranking soon.",
				});
				return;
			}
		} catch {
			// localStorage unavailable (e.g. private browsing) — allow through.
		}

		const normalizedUrl = normalizeWebsiteUrl(websiteUrl);
		if (!normalizedUrl) {
			setSubmitMessage({
				type: "error",
				text: "Please enter your business website URL.",
			});
			return;
		}

		// Layer 4 — Max length: reject oversized inputs before hitting Firestore.
		if (normalizedUrl.length > 2048) {
			setSubmitMessage({
				type: "error",
				text: "The website URL is too long. Please enter a valid URL.",
			});
			return;
		}
		const trimmedEmail = email.trim().toLowerCase();
		if (trimmedEmail.length > 254) {
			setSubmitMessage({
				type: "error",
				text: "The email address is too long. Please enter a valid email.",
			});
			return;
		}

		// Layer 5 — URL + hostname validation: must parse and have a real TLD.
		let hostname: string;
		try {
			hostname = new URL(normalizedUrl).hostname.toLowerCase();
		} catch {
			setSubmitMessage({
				type: "error",
				text: "Please enter a valid website URL.",
			});
			return;
		}
		const tld = hostname.split(".").pop() ?? "";
		if (!hostname.includes(".") || tld.length < 2) {
			setSubmitMessage({
				type: "error",
				text: "Please enter a valid website URL including a domain extension (e.g. .com).",
			});
			return;
		}

		if (!trimmedEmail) {
			setSubmitMessage({
				type: "error",
				text: "Please enter your email address.",
			});
			return;
		}

		setIsSubmitting(true);
		try {
			await addDoc(collection(db, "artifacts", appId, "public", "data", "leads"), {
				email: trimmedEmail,
				websiteUrl: normalizedUrl,
				websiteHost: hostname,
				source: "footer_federal_readiness_scan",
				rankingStatus: "pending",
				createdAt: serverTimestamp(),
			});

			setWebsiteUrl("");
			setEmail("");
			setSubmitMessage({
				type: "success",
				text: "Request received! We'll email your federal readiness ranking soon.",
			});

			// Record cooldown timestamp so the same browser can't spam the form.
			try {
				localStorage.setItem(LEAD_COOLDOWN_KEY, String(Date.now()));
			} catch {
				// noop — localStorage may be blocked in private browsing.
			}
		} catch (error) {
			console.error(
				`[Footer Lead Capture] Failed to save lead for website "${normalizedUrl}":`,
				error
			);
			setSubmitMessage({
				type: "error",
				text: "Unable to submit right now. Please try again in a moment.",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleServiceFieldChange =
		(field: keyof typeof serviceForm) =>
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
			setServiceForm((previous) => ({ ...previous, [field]: event.target.value }));
		};

	const handleCapabilityStatementFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] ?? null;
		if (!file) {
			setCapabilityStatementFile(null);
			return;
		}

		const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
		if (!isPdf) {
			setServiceSubmitMessage({
				type: "error",
				text: "Capability statement must be a PDF file.",
			});
			event.target.value = "";
			return;
		}

		if (file.size > 10 * 1024 * 1024) {
			setServiceSubmitMessage({
				type: "error",
				text: "Capability statement must be 10MB or less.",
			});
			event.target.value = "";
			return;
		}

		setServiceSubmitMessage(null);
		setCapabilityStatementFile(file);
	};

	const handleWebsiteServiceRequestSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setServiceSubmitMessage(null);

		const normalizedServiceWebsiteUrl = normalizeWebsiteUrl(serviceForm.websiteUrl);
		if (serviceForm.websiteUrl.trim()) {
			try {
				new URL(normalizedServiceWebsiteUrl);
			} catch {
				setServiceSubmitMessage({
					type: "error",
					text: "Please provide a valid website URL or leave it blank.",
				});
				return;
			}
		}

		setIsServiceSubmitting(true);
		try {
			const requestsCollectionRef = collection(
				db,
				"artifacts",
				appId,
				"public",
				"data",
				"websiteServiceRequests"
			);
			const requestDocRef = doc(requestsCollectionRef);
			let capabilityStatement:
				| {
						fileName: string;
						fileSize: number;
						fileType: string;
						storagePath: string;
					}
				| null = null;
			if (capabilityStatementFile) {
				const safeFileName = capabilityStatementFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
				const normalizedPdfFileName = safeFileName.toLowerCase().endsWith(".pdf")
					? safeFileName.toLowerCase()
					: `${safeFileName.toLowerCase()}.pdf`;
				const storagePath = `artifacts/${appId}/website-service-requests/${requestDocRef.id}/${Date.now()}-${normalizedPdfFileName}`;
				const statementRef = ref(storage, storagePath);
				await uploadBytes(statementRef, capabilityStatementFile, {
					contentType: "application/pdf",
				});
				capabilityStatement = {
					fileName: capabilityStatementFile.name,
					fileSize: capabilityStatementFile.size,
					fileType: "application/pdf",
					storagePath,
				};
			}

			await setDoc(requestDocRef, {
				companyName: serviceForm.companyName.trim(),
				contactName: serviceForm.contactName.trim(),
				contactEmail: serviceForm.contactEmail.trim().toLowerCase(),
				contactPhone: serviceForm.contactPhone.trim(),
				websiteUrl: normalizedServiceWebsiteUrl || null,
				requestType: serviceForm.requestType,
				briefRequest: serviceForm.briefRequest.trim() || null,
				source: "footer_website_service_cta",
				adminStatus: "new",
				capabilityStatement,
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
			});

			setServiceSubmitMessage({
				type: "success",
				text: "Request submitted. Our team will contact you with next steps.",
			});
			setTimeout(() => {
				setIsServiceModalOpen(false);
				resetWebsiteServiceForm();
			}, 900);
		} catch (error) {
			console.error(
				`[Footer Website Service Request] Failed to submit website service request for company "${serviceForm.companyName.trim()}":`,
				error
			);
			setServiceSubmitMessage({
				type: "error",
				text: "Unable to submit your request right now. Please try again in a moment.",
			});
		} finally {
			setIsServiceSubmitting(false);
		}
	};

	return (
		<footer className="bg-gray-900 text-gray-300 border-t border-gray-700 mt-12 shrink-0">
			{/* Federal Readiness Scan – full-width row, bold and eye-catching */}
			{showLeadCaptureBanner && (
			<div className="bg-gradient-to-r from-blue-900/80 via-blue-800/70 to-blue-900/80 border-y border-blue-600/40">
				<div className="max-w-7xl mx-auto px-4 py-8">
					<div className="max-w-xl mx-auto text-center">
						<h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight mb-2">
							Federal Readiness Scan
						</h2>
						<p className="text-sm text-gray-300 mb-6">
							Share your website and email to receive your federal contracting
							readiness ranking.
						</p>
						<form
							className={`space-y-3 ${linksDisabled ? "pointer-events-none opacity-90" : ""}`}
							onSubmit={linksDisabled ? (e) => e.preventDefault() : handleLeadSubmit}
							aria-disabled={linksDisabled}
						>
							{/* Honeypot — visually hidden; bots fill it, real users don't */}
							<div
								aria-hidden="true"
								style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden", opacity: 0 }}
							>
								<input
									type="text"
									name="contact_email"
									value={leadHoneypot}
									onChange={(e) => setLeadHoneypot(e.target.value)}
									tabIndex={-1}
									autoComplete="off"
								/>
							</div>
							<div className="relative">
								<Globe
									size={16}
									className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300"
								/>
								<input
									type="text"
									inputMode="url"
									value={websiteUrl}
									onChange={(event) => setWebsiteUrl(event.target.value)}
									placeholder="yourcompany.com"
									maxLength={2048}
									className="w-full rounded-lg border border-blue-500/50 bg-gray-900/60 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
									disabled={isSubmitting}
									required
								/>
							</div>
							<div className="relative">
								<Mail
									size={16}
									className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300"
								/>
								<input
									type="email"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
									placeholder="name@company.com"
									maxLength={254}
									className="w-full rounded-lg border border-blue-500/50 bg-gray-900/60 pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
									disabled={isSubmitting}
									required
								/>
							</div>
							<button
								type="submit"
								disabled={isSubmitting}
								className="w-full rounded-lg bg-blue-500 px-4 py-3 text-base font-bold text-white shadow-lg hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-70 transition-colors inline-flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-gray-900"
							>
								{isSubmitting ? (
									<>
										<Loader2 size={18} className="animate-spin" />
										Submitting...
									</>
								) : (
									"Get My Ranking"
								)}
							</button>
						</form>
						{submitMessage && (
							<p
								className={`mt-3 text-sm font-medium ${
									submitMessage.type === "success"
										? "text-green-400"
										: "text-red-400"
								}`}
							>
								{submitMessage.text}
							</p>
						)}
				</div>
			</div>
			</div>
			)}
			{showWebsiteCTABand && (
			<div className="bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 border-b border-blue-700/30">
				<div className="max-w-7xl mx-auto px-4 py-6">
					<div className="max-w-3xl mx-auto rounded-2xl border border-blue-400/30 bg-blue-950/40 backdrop-blur-sm px-5 py-5 shadow-lg">
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
							<div className="text-left">
								<p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-200 mb-2">
									<Sparkles size={14} />
									Need a stronger federal-ready website?
								</p>
								<h3 className="text-lg md:text-xl font-bold text-white leading-tight">
									No website yet, or need upgrades to win more subcontracting work?
								</h3>
								<p className="mt-2 text-sm text-blue-100/90">
									Request website development or enhancement services and send your
									capability statement so our team can tailor recommendations to your
									NAICS focus and federal goals.
								</p>
							</div>
							<button
								type="button"
								disabled={linksDisabled}
								onClick={() => setIsServiceModalOpen(true)}
								className="inline-flex items-center justify-center rounded-lg bg-blue-500 hover:bg-blue-400 disabled:bg-blue-800/60 disabled:text-blue-200/70 disabled:cursor-not-allowed px-5 py-3 font-bold text-white shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
							>
								Request Website Help
							</button>
						</div>
					</div>
				</div>
			</div>
			)}

			{/* Newsletter subscription band */}
			{showNewsletterSection && (
				<div className="bg-gradient-to-r from-[#030F2F] via-[#151B27] to-[#0F131D] border-b border-blue-500/40">
					<div className="max-w-7xl mx-auto px-4 py-10">
						<div className="max-w-2xl mx-auto text-center">
							<h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight mb-2">
								{newsletterHeading ?? 'Stay in the loop'}
							</h2>
							<p className="text-sm text-blue-100 mb-6">
								{newsletterDescription ?? 'Get government contracting insights, tips, and updates delivered to your inbox.'}
							</p>
							<div className={linksDisabled ? 'pointer-events-none opacity-90' : ''} aria-disabled={linksDisabled}>
								<NewsletterSubscribeForm
									source="footer_newsletter"
									heading=""
									description=""
									buttonLabel="Subscribe"
									variant="default"
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="max-w-7xl mx-auto px-4 pt-6 pb-3">
				<div className={`grid grid-cols-1 gap-8 ${footerColumns.length > 0 ? `md:grid-cols-${Math.min(footerColumns.length + 1, 4)}` : 'md:grid-cols-3'}`}>
					{/* Brand column */}
					<div>
						<BrandLogo variant="white" className="h-10 w-auto mb-3" alt={effectiveBrandName} />
						<p className="text-sm text-gray-400 mb-4">
							{brandDescription}
						</p>
						{/* Social links — dynamic or hardcoded fallback */}
						<div className="flex items-center gap-3">
							{socialLinks !== null && socialLinks.length > 0 ? (
								socialLinks.filter((s) => s.isVisible).map((s) => (
									linksDisabled ? (
										<span key={s.id} className="text-gray-500" aria-hidden="true">{SOCIAL_ICON[s.platform]}</span>
									) : (
										<a
											key={s.id}
											href={s.url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
											aria-label={`${effectiveBrandName} on ${SOCIAL_LABEL[s.platform]}`}
										>
											{SOCIAL_ICON[s.platform]}
										</a>
									)
								))
							) : (
								DEFAULT_SOCIAL_LINKS.map((item) => {
									const iconNode = SOCIAL_ICON[item.platform];
									const label = SOCIAL_LABEL[item.platform];
									return linksDisabled ? (
										<span key={item.url} className="text-gray-500" aria-hidden="true">{iconNode}</span>
									) : (
										<a
											key={item.url}
											href={item.url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
											aria-label={`${effectiveBrandName} on ${label}`}
										>
											{iconNode}
										</a>
									);
								})
							)}
						</div>
					</div>

					{/* Dynamic CMS footer columns */}
					{footerColumns.length > 0 ? (
						footerColumns.map(({ group, items }) => (
							<div key={group.id}>
								<h3 className="font-bold text-white mb-3">{group.name}</h3>
								<ul className="space-y-2 text-sm">
									{items.map((item) => {
										const href = item.slug || item.url || '#';
										const isExternal = !!(item.url && !item.slug);
										return (
											<li key={item.id}>
												{item.type === 'text' ? (
													<p className="text-gray-400 text-sm leading-relaxed flex items-start gap-1.5">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-50 shrink-0 mt-0.5" />}
														{item.label}
													</p>
												) : item.type === 'html' && item.embedCode ? (
													<div
														className="text-gray-400 text-sm leading-relaxed [&_a]:text-gray-400 [&_a:hover]:text-white [&_a]:transition-colors"
														dangerouslySetInnerHTML={{
															__html: placeholderReplacements
																? replaceCmsPlaceholders(item.embedCode, placeholderReplacements)
																: item.embedCode,
														}}
													/>
												) : linksDisabled ? (
													<span className="text-gray-400 flex items-center gap-1.5 cursor-default">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-50 shrink-0" />}
														{item.label}
													</span>
												) : isExternal ? (
													<a href={href} target={item.target ?? '_self'} rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-60 shrink-0" />}
														{item.label}
														{item.target === '_blank' && <ExternalLink size={12} className="opacity-50" />}
													</a>
												) : (
													<Link to={href} className="text-gray-400 hover:text-white transition-colors flex items-center gap-1.5">
														{item.icon && <NavIconRenderer name={item.icon} size={13} className="opacity-60 shrink-0" />}
														{item.label}
													</Link>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						))
					) : (
						/* Hardcoded fallback columns */
						<>
						<div>
						<h3 className="font-bold text-white mb-3">Legal & Information</h3>
						<ul className="space-y-2 text-sm">
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 flex items-center gap-2 cursor-default">
										<Info size={14} />
										About
									</span>
								) : (
									<Link
										to="/about-us"
										className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
									>
										<Info size={14} />
										About
									</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 flex items-center gap-2 cursor-default">
										<Shield size={14} />
										Terms of Service
									</span>
								) : (
									<Link
										to="/terms-of-service"
										className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
									>
										<Shield size={14} />
										Terms of Service
									</Link>
								)}
							</li>
							<li>
								{linksDisabled ? (
									<span className="text-gray-400 flex items-center gap-2 cursor-default">
										<Lock size={14} />
										Privacy Policy
									</span>
								) : (
									<Link
										to="/privacy-policy"
										className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
									>
										<Lock size={14} />
										Privacy Policy
									</Link>
								)}
							</li>
						</ul>
					</div>
					<div>
						<h3 className="font-bold text-white mb-3">Support</h3>
						<p className="text-sm text-gray-400 mb-2">
							For questions about the platform, please contact the
							administrators.
						</p>
						<div className="space-y-2">
							{linksDisabled ? (
								<span className="inline-flex items-center gap-2 text-sm text-gray-400 cursor-default">
									<Phone size={14} />
									{displayPhone}
								</span>
							) : (
								<a
									href={telHref}
									className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
									aria-label={`Call ${effectiveBrandName}: ${displayPhone}`}
								>
									<Phone size={14} aria-hidden />
									{displayPhone}
								</a>
							)}
							{displayEmail && (
								linksDisabled ? (
									<span className="block text-sm text-gray-400 cursor-default">{displayEmail}</span>
								) : (
									<a
										href={`mailto:${displayEmail}`}
										className="block text-sm text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
										aria-label={`Email ${effectiveBrandName}: ${displayEmail}`}
									>
										{displayEmail}
									</a>
								)
							)}
							{displayAddress && (
								<p className="text-sm text-gray-400 whitespace-pre-line">{displayAddress}</p>
							)}
						</div>
					</div>
					</>
					)}
				</div>
				<div className="border-t border-gray-700 mt-4 pt-4 pb-0 text-xs text-gray-500 text-center space-y-2">
					<p>{copyrightText}</p>
				</div>
			</div>
			{isServiceModalOpen && (
				<div
					className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
					role="dialog"
					aria-modal="true"
					aria-labelledby="website-service-modal-title"
					onClick={(event) => {
						if (event.target === event.currentTarget && !isServiceSubmitting) {
							closeWebsiteServiceModal();
						}
					}}
				>
					<div className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-blue-200 bg-white shadow-2xl">
						<div className="flex items-center justify-between bg-gradient-to-r from-blue-700 to-blue-600 px-5 py-4">
							<div>
								<h3
									id="website-service-modal-title"
									className="text-lg font-bold text-white"
								>
									Website Development & Enhancement Request
								</h3>
								<p className="text-sm text-blue-100">
									Submit a quick request now. We will follow up to collect full
									project details.
								</p>
							</div>
							<button
								type="button"
								onClick={closeWebsiteServiceModal}
								disabled={isServiceSubmitting}
								className="rounded-lg p-2 text-white/80 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 disabled:opacity-50"
								aria-label="Close website service request modal"
							>
								<X size={20} />
							</button>
						</div>
						<form
							className="overflow-y-auto max-h-[calc(90vh-84px)]"
							onSubmit={handleWebsiteServiceRequestSubmit}
						>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
								<div>
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Company name *
									</label>
									<div className="relative">
										<Building2
											size={16}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
										/>
										<input
											required
											type="text"
											value={serviceForm.companyName}
											onChange={handleServiceFieldChange("companyName")}
											className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
											placeholder="Acme Federal Solutions"
										/>
									</div>
								</div>
								<div>
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Contact name *
									</label>
									<div className="relative">
										<User2
											size={16}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
										/>
										<input
											required
											type="text"
											value={serviceForm.contactName}
											onChange={handleServiceFieldChange("contactName")}
											className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
											placeholder="Jane Smith"
										/>
									</div>
								</div>
								<div>
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Contact email *
									</label>
									<div className="relative">
										<Mail
											size={16}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
										/>
										<input
											required
											type="email"
											value={serviceForm.contactEmail}
											onChange={handleServiceFieldChange("contactEmail")}
											className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
											placeholder="name@company.com"
										/>
									</div>
								</div>
								<div>
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Phone number *
									</label>
									<div className="relative">
										<Phone
											size={16}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
										/>
										<input
											required
											type="tel"
											value={serviceForm.contactPhone}
											onChange={handleServiceFieldChange("contactPhone")}
											className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
											placeholder="+1 (555) 123-4567"
										/>
									</div>
								</div>
								<div>
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Current website (optional)
									</label>
									<div className="relative">
										<Globe
											size={16}
											className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
										/>
									<input
										type="text"
										value={serviceForm.websiteUrl}
										onChange={handleServiceFieldChange("websiteUrl")}
										className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
										placeholder="yourcompany.com"
									/>
									</div>
								</div>
								<div>
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Request type *
									</label>
									<select
										required
										value={serviceForm.requestType}
										onChange={handleServiceFieldChange("requestType")}
										className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
									>
										<option value="new_website">New website development</option>
										<option value="website_enhancement">Website enhancement</option>
										<option value="redesign_and_enhancement">
											Redesign + enhancement
										</option>
									</select>
								</div>
								<div className="md:col-span-2">
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Tell us what you need (optional)
									</label>
									<textarea
										rows={3}
										value={serviceForm.briefRequest}
										onChange={handleServiceFieldChange("briefRequest")}
										className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
										placeholder="Share a quick note. We'll gather full details during follow-up."
									/>
								</div>
								<div className="md:col-span-2">
									<label className="mb-1.5 block text-sm font-medium text-gray-700">
										Capability statement PDF (optional)
									</label>
									<div className="rounded-xl border border-dashed border-blue-300 bg-blue-50/60 px-4 py-4">
										<div className="flex items-start gap-3">
											<FileText size={18} className="text-blue-700 mt-0.5" />
											<div className="flex-1">
												<input
													type="file"
													accept="application/pdf,.pdf"
													onChange={handleCapabilityStatementFileChange}
													className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white hover:file:bg-blue-700"
												/>
												<p className="mt-1 text-xs text-gray-600">
													PDF format only, up to 10MB.
												</p>
												{capabilityStatementFile && (
													<p className="mt-1 text-xs font-medium text-green-700">
														Selected: {capabilityStatementFile.name}
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="border-t border-gray-200 px-5 py-4 bg-gray-50">
								{serviceSubmitMessage && (
									<p
										className={`mb-3 text-sm font-medium ${
											serviceSubmitMessage.type === "success"
												? "text-green-700"
												: "text-red-600"
										}`}
									>
										{serviceSubmitMessage.text}
									</p>
								)}
								<div className="flex items-center justify-end gap-2">
									<button
										type="button"
										onClick={closeWebsiteServiceModal}
										disabled={isServiceSubmitting}
										className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
									>
										Cancel
									</button>
									<button
										type="submit"
										disabled={isServiceSubmitting}
										className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-70"
									>
										{isServiceSubmitting ? (
											<>
												<Loader2 size={16} className="animate-spin" />
												Submitting...
											</>
										) : (
											"Submit Request"
										)}
									</button>
								</div>
							</div>
						</form>
					</div>
				</div>
			)}
		</footer>
	);
};
